import type { Tone } from '@didto/ui';
import type { Database } from './database.types';

/**
 * One colour per state, the same on every screen (ADR-037). A badge always says its state in words
 * too: the colour only helps the eye. Danger is for what went wrong or was taken back (voided, cancelled
 * on a ticket, a red review item); warning is for what needs someone (unpaid, not sent, to settle);
 * success is for what is done (paid, ready, served, completed); info for work in progress; neutral for
 * what is simply over (an order cancelled before it was sent, a method archived).
 */
type Enums = Database['public']['Enums'];

export const ORDER_STATUS_TONE: Record<Enums['order_status'], Tone> = {
  open: 'info',
  completed: 'success',
  cancelled: 'neutral',
  voided: 'danger',
};

export const PAYMENT_STATUS_TONE: Record<Enums['payment_status'], Tone> = {
  unpaid: 'warning',
  partially_paid: 'warning',
  paid: 'success',
  written_off: 'neutral',
  partially_refunded: 'info',
  refunded: 'neutral',
};

/** An order's kitchen and bar progress: ready stands out for the cashier (Phase 5). */
export function fulfilmentTone(f: Enums['fulfilment_status']): Tone {
  switch (f) {
    case 'ready':
    case 'served':
      return 'success';
    case 'partially_ready':
    case 'partially_served':
      return 'accent';
    case 'not_sent':
      return 'warning';
    case 'not_required':
      return 'neutral';
    default:
      return 'info';
  }
}

export function ticketTone(status: Enums['ticket_status']): Tone {
  switch (status) {
    case 'ready':
    case 'served':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** A line or a payment as recorded: taken back reads as danger, given back as warning. */
export const LEDGER_ROW_TONE = {
  sent: 'success',
  not_sent: 'warning',
  voided: 'danger',
  refunded: 'warning',
  cancelled: 'neutral',
  archived: 'neutral',
} as const satisfies Record<string, Tone>;
